import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, ToggleButtonGroup, ToggleButton, InputAdornment,
  CircularProgress, Autocomplete, Container, Avatar
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { es } from 'date-fns/locale';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HttpsIcon from '@mui/icons-material/Https';
import { message } from 'antd';
import Swal from 'sweetalert2';

import LoadingInformation from '../../Components/LoadingInformation';

function toMySQLDatetime(dateString) {
  const date = new Date(dateString);
  // Ajustar para la zona horaria local para evitar diferencias UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function InternalCashFlowManager() {
  // --- Estados de Carga y Permisos ---
  const [isLoading, setIsLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // --- Estados de Datos ---
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);

  // --- Estados del Formulario ---
  const [flowType, setFlowType] = useState('egreso');
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [description, setDescription] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date());
  const [relatedEmployee, setRelatedEmployee] = useState(null);
  const [receiptReference, setReceiptReference] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // --- LÓGICA DE PERMISOS Y CARGA DE DATOS ---
  useEffect(() => {
    // Función principal que orquesta la inicialización
    const initializeComponent = async () => {
      // 1. Verificar permisos primero
      const hasPermission = await checkPermissions();

      // 2. Si hay permisos, cargar los datos del componente
      if (hasPermission) {
        await fetchData();
      }

      // 3. Finalizar el estado de carga general
      setIsLoading(false);
    };

    const checkPermissions = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        messageApi.error('Sesión no encontrada. Redirigiendo al login...');
        setPermissionError(true);
        setTimeout(() => { window.location.href = '/sign-in'; }, 2000);
        return false;
      }

      try {
        const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 403) {
          messageApi.error('No tienes permisos para acceder a esta sección.');
          setPermissionError(true);
          return false; // Permiso denegado
        }

        if (!response.ok) {
          throw new Error('Error al verificar permisos.');
        }

        return true; // Permiso concedido

      } catch (error) {
        messageApi.error(error.message || 'Error de conexión al verificar permisos.');
        setPermissionError(true);
        return false;
      }
    };

    const fetchData = async () => {
      try {
        const [categoriesRes, employeesRes] = await Promise.all([
          fetch('https://dprincess-training.servehttp.com/finances/internal-categories', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('https://dprincess-training.servehttp.com/users/employees-list', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        if (!categoriesRes.ok || !employeesRes.ok) {
          throw new Error('No se pudieron cargar los datos para el formulario.');
        }

        const categoriesData = await categoriesRes.json();
        const employeesData = await employeesRes.json();

        setCategories(categoriesData);
        setEmployees(employeesData);

      } catch (error) {
        console.error("Error al cargar datos:", error);
        message.error(error.message);
        // Ponemos el error de permiso para bloquear el renderizado del formulario
        setPermissionError(true);
      }
    };

    initializeComponent();
  }, []); // El array vacío asegura que se ejecute solo una vez


  // --- Lógica del Componente (sin cambios) ---
  const filteredCategories = useMemo(() => categories.filter(cat => cat.flow_type === flowType && cat.is_active), [flowType, categories]);
  useEffect(() => { setCategoryId(''); }, [flowType]);

  const validateForm = () => {
    const newErrors = {};
    if (!categoryId) newErrors.categoryId = 'Debe seleccionar una categoría';
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) newErrors.amount = 'El monto debe ser un número positivo';
    if (!description.trim()) newErrors.description = 'La descripción es obligatoria';

    // Validar empleado si el campo se muestra
    if (showEmployeeField && !relatedEmployee) {
      newErrors.relatedEmployee = 'Debes asignar un empleado a esta transacción';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setCategoryId(''); setAmount(''); setPaymentMethod('efectivo'); setDescription('');
    setTransactionDate(new Date()); setRelatedEmployee(null); setReceiptReference('');
    setSupplierName(''); setErrors({});
  };

  const handleRegisterTransaction = async () => {
    if (!validateForm()) {
      message.error('Por favor, complete los campos obligatorios.');
      return;
    }
    const confirmResult = await Swal.fire({
      title: '¿Confirmar Transacción?',
      html: `Estás a punto de registrar un <strong>${flowType.toUpperCase()}</strong> por <strong>RD$ ${parseFloat(amount).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</strong>.<br>¿Deseas continuar?`,
      icon: 'question', showCancelButton: true, confirmButtonColor: '#4CAF50',
      cancelButtonColor: '#F44336', confirmButtonText: 'Sí, Registrar', cancelButtonText: 'Cancelar'
    });
    if (!confirmResult.isConfirmed) return;
    setIsSubmitting(true);
    Swal.fire({ title: 'Registrando...', text: 'Por favor, espera.', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });
    const payload = {
      amount: parseFloat(amount), 
      paymentMethod, 
      flow_type: flowType, 
      description: description.trim(),
      transaction_date: toMySQLDatetime(transactionDate), 
      category_ID: categoryId,
      related_employee_ID: relatedEmployee ? relatedEmployee.employeeID : null,
      receipt_reference: receiptReference.trim() || null, supplier_name: supplierName.trim() || null,
    };
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/finances/operational-transactions/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error del servidor');
      }
      await response.json();
      Swal.close();
      await Swal.fire({
        title: '¡Éxito!',
        text: 'La transacción se ha registrado correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      });
      resetForm();
    } catch (error) {
      Swal.close();
      await Swal.fire({
        title: 'Error',
        text: error.message || 'No se pudo completar el registro.',
        icon: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showEmployeeField = useMemo(() => {
    const selectedCategory = categories.find(c => c.category_ID === categoryId);
    if (!selectedCategory) return false;
    const categoryName = selectedCategory.category_name.toLowerCase();

    // Categorías que requieren empleado
    const employeeRequiredCategories = [
      'sueldo', 'préstamo', 'viático', 'dieta', 'comisión', 'bono',
      'transporte', 'combustible'
    ];

    return employeeRequiredCategories.some(keyword => categoryName.includes(keyword));
  }, [categoryId, categories]);

  // --- RENDERIZADO CONDICIONAL ---

  if (isLoading) <LoadingInformation />

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}><HttpsIcon sx={{ fontSize: 40 }} /></Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>No tienes permiso para acceder a esta sección</Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>Esta información solo está disponible para usuarios con permisos de administrador.</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {contextHolder}
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, maxWidth: '900px', margin: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, borderBottom: '1px solid #ddd', pb: 2 }}>
            <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h5" component="h1">Gestión de Flujo de Efectivo Interno</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ToggleButtonGroup color="primary" value={flowType} exclusive onChange={(e, val) => val && setFlowType(val)} fullWidth>
                <ToggleButton value="egreso" sx={{ flexGrow: 1 }}>Egreso / Gasto</ToggleButton>
                <ToggleButton value="ingreso" sx={{ flexGrow: 1 }}>Ingreso Interno</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.categoryId}>
                <InputLabel>Categoría *</InputLabel>
                <Select label="Categoría *" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={filteredCategories.length === 0}>
                  {filteredCategories.map((cat) => (
                    <MenuItem key={cat.category_ID} value={cat.category_ID}>{cat.category_name}</MenuItem>
                  ))}
                </Select>
                {errors.categoryId && <Typography color="error" variant="caption">{errors.categoryId}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Monto *" type="number" fullWidth value={amount} onChange={(e) => setAmount(e.target.value)} error={!!errors.amount} helperText={errors.amount} InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descripción detallada *" fullWidth multiline rows={2} value={description} onChange={(e) => setDescription(e.target.value)} error={!!errors.description} helperText={errors.description} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Método de Pago</InputLabel>
                <Select value={paymentMethod} label="Método de Pago" onChange={(e) => setPaymentMethod(e.target.value)}>
                  <MenuItem value="efectivo">Efectivo</MenuItem>
                  <MenuItem value="tarjeta">Tarjeta</MenuItem>
                  <MenuItem value="transferencia">Transferencia</MenuItem>
                  <MenuItem value="otro">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DateTimePicker label="Fecha y Hora *" value={transactionDate} onChange={(newDate) => setTransactionDate(newDate)} renderInput={(params) => <TextField {...params} fullWidth />} />
            </Grid>
            {showEmployeeField && (
              <Grid item xs={12} md={6}>
                <Autocomplete 
                  options={employees} 
                  getOptionLabel={(option) => option.employeeName || ''} 
                  value={relatedEmployee} 
                  onChange={(e, val) => setRelatedEmployee(val)} 
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Empleado Relacionado *" 
                      error={!!errors.relatedEmployee}
                      helperText={errors.relatedEmployee}
                    />
                  )} 
                  isOptionEqualToValue={(option, value) => option.employeeID === value.employeeID} 
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField label="Nombre del Proveedor (Opcional)" fullWidth value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Referencia de Recibo/Factura/Transferencia (Opcional)" fullWidth value={receiptReference} onChange={(e) => setReceiptReference(e.target.value)} />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button variant="outlined" color="secondary" onClick={resetForm} disabled={isSubmitting}>Limpiar Formulario</Button>
              <Button variant="contained" size="large" onClick={handleRegisterTransaction} disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}>
                {isSubmitting ? 'Registrando...' : `Registrar ${flowType === 'egreso' ? 'Gasto' : 'Ingreso'}`}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}

export default InternalCashFlowManager;